import React from "react";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, X, StarIcon } from "lucide-react";
import { formContent } from "../../../utils/formContent";

const PositiveSubmit = ({
  isOpen,
  onClose,
  message = "Vielen Dank für Ihr Feedback!",
  autoCloseDelay = 5000,
  form,
  color,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const lang = form?.language || "DE";
  const t = formContent[lang];

  useEffect(() => {
    if (isOpen && autoCloseDelay > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
      return () => clearTimeout(timer);
    }
  }, [isOpen, onClose, autoCloseDelay]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="relative max-w-md w-full mx-4 bg-white rounded-xl shadow-2xl overflow-hidden"
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors"
              aria-label="Schließen"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-6 text-center">
              <div className="flex justify-center mb-4">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: "spring",
                    delay: 0.2,
                    damping: 10,
                    stiffness: 200,
                  }}
                  className="rounded-full bg-green-100 p-3"
                >
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </motion.div>
                </motion.div>
              </div>

              <motion.h3
                className="text-xl font-bold text-gray-800 mb-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                {t.titleP}
              </motion.h3>

              <motion.p
                className="text-gray-600 mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                {message}
              </motion.p>

              <motion.div
                className="flex justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                <div className="flex space-x-1">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <motion.div
                      key={i}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{
                        delay: 0.8 + i * 0.1,
                        type: "spring",
                        stiffness: 300,
                      }}
                    >
                      <svg
                        className="w-6 h-6 text-yellow-400 fill-current"
                        viewBox="0 0 24 24"
                      >
                        <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                      </svg>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
              {form.enableGoogleRedirect && form.googleReviewUrl && (
                <div className="mt-8 space-y-4">
                  <div className="text-center">
                    <h3 className="text-base font-medium text-gray-800">
                      {t.googleTitle}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {t.googleDescription}
                    </p>
                  </div>

                  <a
                    href={form.googleReviewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ backgroundColor: color }}
                    className="group flex items-center justify-center gap-2 w-full max-w-xs mx-auto px-4 py-3 rounded-lg transition-all duration-300 text-white shadow-md hover:shadow-lg"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    <div className="flex items-center justify-center bg-white rounded-full p-1.5 transition-all duration-300 group-hover:scale-110">
                      <svg
                        viewBox="0 0 24 24"
                        width="18"
                        height="18"
                        className="text-orange-600"
                        fill="currentColor"
                      >
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
                        <path d="M12 4l2.44 7.51h7.89l-6.39 4.63 2.44 7.51L12 18.39l-6.38 4.63 2.44-7.51L1.67 11.5h7.89z" />
                      </svg>
                    </div>
                    <span className="font-medium">{t.googleCta}</span>
                    <div
                      className={`flex transition-all duration-300 ${
                        isHovered ? "translate-x-1" : ""
                      }`}
                    >
                      <StarIcon className="w-5 h-5 text-yellow-300 fill-yellow-300" />
                    </div>
                  </a>

                  <p className="text-xs text-center text-gray-500 mt-2">
                    {t.googleNote}
                  </p>
                </div>
              )}
            </div>

            <motion.div
              className="h-2 bg-gradient-to-r from-orange-500 to-orange-600"
              initial={{ width: "100%" }}
              animate={{ width: "0%" }}
              transition={{ duration: autoCloseDelay / 1000, ease: "linear" }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PositiveSubmit;
